import { forwardRef } from "react";

import { Avatar, AvatarProps, Theme, SxProps, useTheme } from "@mui/material";

import { FormIcon, FormIconProps } from "@/forms-design-system/FormIcon";
import { mergeSx } from "@/forms-design-system/helpers";
import { User } from "@/icons/outlined";
import { IconElement } from "@/icons/types";

type FormAvatarProps = Omit<AvatarProps, "size" | "children"> & {
  showFallbackIcon?: boolean;
  components?: {
    fallbackIcon?: {
      sx?: SxProps<Theme>;
      size?: FormIconProps["size"];
      icon?: IconElement;
    };
  };
};

export const FormAvatar = forwardRef<HTMLDivElement, FormAvatarProps>(
  ({ src, sx, components, showFallbackIcon = false, ...rest }, ref) => {
    const theme = useTheme();

    const styles = mergeSx(
      {
        width: "32px",
        height: "32px",
        borderRadius: theme.radius(0.5),
        backgroundColor: theme.palette.formsComponents.avatar.fallbackBackgroundColor,
        color: theme.palette.formsComponents.avatar.fallbackIconColor,
      },
      sx
    );

    if (!showFallbackIcon && !src) {
      return null;
    }

    return (
      <Avatar src={src} {...rest} ref={ref} sx={styles}>
        {showFallbackIcon && (
          <FormIcon size={components?.fallbackIcon?.size ?? "medium"} sx={components?.fallbackIcon?.sx}>
            {components?.fallbackIcon?.icon ?? <User />}
          </FormIcon>
        )}
      </Avatar>
    );
  }
);

FormAvatar.displayName = "FormAvatar";
